/**
 * Cliente Neto en pedidos: lectura del flag y transformación de líneas.
 * Crear y editar comparten estas funciones vía OrderForm / useOrderProducts.
 */
import type { LineItem, OrderData } from '../types/orderFormTypes'
import type { CotizacionData } from '../types/cotizacionFormTypes'
import {
  applyClienteNetoPrecios,
  stripClienteNetoPrecios,
  calculateTotals,
  getLineItemIvaRateFromDetails
} from './orderFormUtils'
import { calculateTotals as calculateCotizacionTotals } from './cotizacionCalculations'
import { parseClienteNetoFlag } from './clienteNetoFlag'

export { parseClienteNetoFlag }

/** IVA usado cuando aún no hay detalle de producto (debe re-sync al cargar catálogo). */
export const CLIENTE_NETO_FALLBACK_IVA_RATE = 0.19

const IVA_RATE_EPSILON = 0.0001

export interface ClienteNetoAppliedRegistry {
  skus: Set<string>
  ivaRateBySku: Map<string, number>
}

export function createClienteNetoAppliedRegistry(): ClienteNetoAppliedRegistry {
  return { skus: new Set(), ivaRateBySku: new Map() }
}

export function clearClienteNetoAppliedRegistry(registry: ClienteNetoAppliedRegistry): void {
  registry.skus.clear()
  registry.ivaRateBySku.clear()
}

export function markClienteNetoAppliedForLines(
  registry: ClienteNetoAppliedRegistry,
  items: LineItem[],
  productDetails: { [key: string]: unknown }
): void {
  items.forEach((item) => {
    const sku = lineItemSku(item)
    if (!sku) return
    registry.skus.add(sku)
    registry.ivaRateBySku.set(sku, getLineItemIvaRateFromDetails(item, productDetails))
  })
}

export function unmarkClienteNetoAppliedSku(registry: ClienteNetoAppliedRegistry, sku: string): void {
  if (!sku) return
  registry.skus.delete(sku)
  registry.ivaRateBySku.delete(sku)
}

function resolveLineItemIvaRate(
  item: LineItem,
  productDetails: { [key: string]: unknown }
): number {
  return getLineItemIvaRateFromDetails(item, productDetails)
}

/** Corrige precio CN cuando cambió la tasa de IVA del catálogo (p. ej. 19% → 5%). */
function reapplyClienteNetoLineItem(
  item: LineItem,
  productDetails: { [key: string]: unknown }
): LineItem {
  const [base] = stripClienteNetoPrecios([item], productDetails)
  const [applied] = applyClienteNetoPrecios([base], productDetails)
  return applied
}

export function getSkusNeedingClienteNetoSync(
  lines: LineItem[],
  productDetails: { [key: string]: unknown },
  registry: ClienteNetoAppliedRegistry
): { newSkus: string[]; reapplySkus: string[] } {
  const newSkus: string[] = []
  const reapplySkus: string[] = []

  for (const item of lines) {
    const sku = lineItemSku(item)
    if (!sku || !productDetails[sku]) continue

    const currentRate = resolveLineItemIvaRate(item, productDetails)
    const storedRate = registry.ivaRateBySku.get(sku)

    if (!registry.skus.has(sku)) {
      newSkus.push(sku)
      continue
    }

    if (
      storedRate !== undefined &&
      Math.abs(currentRate - storedRate) > IVA_RATE_EPSILON
    ) {
      reapplySkus.push(sku)
    }
  }

  return { newSkus, reapplySkus }
}

export function applyClienteNetoSyncToLineItems(
  lines: LineItem[],
  productDetails: { [key: string]: unknown },
  newSkus: string[],
  reapplySkus: string[]
): LineItem[] {
  const reapplySet = new Set(reapplySkus)
  const newSet = new Set(newSkus)

  return lines.map((item) => {
    const sku = lineItemSku(item)
    if (!sku) return item

    if (reapplySet.has(sku)) {
      return reapplyClienteNetoLineItem(item, productDetails)
    }
    if (newSet.has(sku)) {
      const [applied] = applyClienteNetoPrecios([item], productDetails)
      return applied
    }
    return item
  })
}

export function isClienteNetoActive(
  extraDetails?: { cliente_neto?: unknown } | null
): boolean {
  return parseClienteNetoFlag(extraDetails?.cliente_neto)
}

/** Activa CN: apply sobre precio base. Desactiva CN: strip (un nivel de IVA embebido). */
function syncLineItemsForClienteNeto(
  items: LineItem[],
  productDetails: { [key: string]: unknown },
  clienteNeto: boolean
): LineItem[] {
  if (!items.length) return items
  return clienteNeto
    ? applyClienteNetoPrecios(items, productDetails)
    : stripClienteNetoPrecios(items, productDetails)
}

/** Aplica CN solo a las líneas indicadas (p. ej. producto recién agregado). */
export function applyClienteNetoToLineItems(
  items: LineItem[],
  productDetails: { [key: string]: unknown },
  onlySkus?: string[]
): LineItem[] {
  if (!items.length) return items
  const skuSet = onlySkus?.length ? new Set(onlySkus) : null
  return items.map((item) => {
    const sku = item.sku || item.product_id
    if (skuSet && !skuSet.has(sku)) return item
    const [applied] = applyClienteNetoPrecios([item], productDetails)
    return applied
  })
}

export function recalculateOrderTotalWithClienteNeto(
  lineItems: LineItem[],
  productDetails: { [key: string]: unknown },
  costoEnvio: string,
  clienteNeto: boolean
): string {
  const totals = calculateTotals(
    lineItems,
    productDetails,
    costoEnvio,
    clienteNeto
  )
  return totals.total
}

export interface ClienteNetoToggleResult {
  lineItems: LineItem[]
  extraDetails: OrderData['extraDetails']
  total: string
  changed: boolean
}

/**
 * Estado tras activar/desactivar el switch (precios + flag + total).
 */
export function applyClienteNetoToggle(
  prev: OrderData,
  checked: boolean,
  productDetails: { [key: string]: unknown },
  costoEnvio: string
): ClienteNetoToggleResult {
  const wasActive = isClienteNetoActive(prev.extraDetails)
  const items = [...(prev.lineItems || [])]
  const extraDetails = {
    ...(prev.extraDetails || {}),
    cliente_neto: checked
  }

  if (checked === wasActive) {
    return {
      lineItems: items,
      extraDetails,
      total: prev.total || '0',
      changed: false
    }
  }

  const synced = syncLineItemsForClienteNeto(items, productDetails, checked)
  const total = recalculateOrderTotalWithClienteNeto(
    synced,
    productDetails,
    costoEnvio,
    checked
  )

  return {
    lineItems: synced,
    extraDetails,
    total,
    changed: true
  }
}

export function lineItemSku(item: LineItem): string {
  return String(item.sku || item.product_id || '')
}

export interface ClienteNetoCotizacionToggleResult {
  line_items: LineItem[]
  extraDetails: CotizacionData['extraDetails']
  total: string
  costoEnvio: string
  changed: boolean
}

/**
 * Toggle Cliente Neto en cotizaciones — misma lógica que pedidos:
 * solo transforma precios de líneas; el costo de envío NO se multiplica/divide.
 */
export function applyClienteNetoToggleCotizacion(
  prev: CotizacionData,
  checked: boolean,
  productDetails: { [key: string]: unknown },
  costoEnvio: string
): ClienteNetoCotizacionToggleResult {
  const wasActive = isClienteNetoActive(prev.extraDetails)
  const items = [...(prev.line_items || [])]
  const extraDetails = {
    ...(prev.extraDetails || {}),
    cliente_neto: checked
  }

  if (checked === wasActive) {
    return {
      line_items: items,
      extraDetails,
      total: prev.total || '0',
      costoEnvio,
      changed: false
    }
  }

  const synced = syncLineItemsForClienteNeto(items, productDetails, checked)
  const totals = calculateCotizacionTotals(
    synced,
    costoEnvio,
    productDetails,
    checked
  )

  return {
    line_items: synced,
    extraDetails: {
      ...extraDetails,
      costo_envio: costoEnvio
    },
    total: totals.total,
    costoEnvio,
    changed: true
  }
}
